import React, { useId, useLayoutEffect, useRef, useState } from "react";
import {
  CheckCircle2, Clock, ExternalLink, IndianRupee, MapPin, Monitor, Pencil, PlayCircle, UserPlus, Users,
} from "lucide-react";
import { AppButton, AppCard, AppCardContent, AppCardHeader, AppCardTitle, cn } from "../../ui";
import RichText, { hasRichContent } from "../../Common/RichText";
import { DASH } from "../../../utils/format";

const COLLAPSED_PX = 160; // tw-max-h-40
const HIGHLIGHTS_SHOWN = 6;

/**
 * Long copy, cut to a fixed height with a fade until the reader asks for the
 * rest. The cut is MEASURED, so a short description shows no button at all.
 */
const Expandable = ({ children }) => {
  const ref = useRef(null);
  const id = useId();
  const [overflows, setOverflows] = useState(false);
  const [open, setOpen] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    // The slack stops a button that would reveal one more line.
    const measure = () => setOverflows(el.scrollHeight > COLLAPSED_PX + 24);
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const clipped = overflows && !open;
  return (
    <div>
      <div id={id} ref={ref} className={cn("tw-relative", clipped && "tw-max-h-40 tw-overflow-hidden")}>
        {children}
        {clipped && (
          <div
            aria-hidden="true"
            className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-12 tw-bg-gradient-to-t tw-from-surface-raised"
          />
        )}
      </div>
      {overflows && (
        <AppButton
          size="xs"
          variant="ghost"
          className="tw-mt-1 tw--ml-2"
          aria-expanded={open}
          aria-controls={id}
          onClick={() => setOpen((o) => !o)}
        >
          {open ? "Show less" : "Show more"}
        </AppButton>
      )}
    </div>
  );
};

const SectionTitle = ({ children }) => (
  <h3 className="tw-m-0 tw-mb-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-caps tw-text-content-subtle">
    {children}
  </h3>
);

const Highlights = ({ items }) => {
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, HIGHLIGHTS_SHOWN);
  return (
    <>
      <ul className="tw-m-0 tw-grid tw-list-none tw-gap-x-6 tw-gap-y-2 tw-p-0 min-[768px]:tw-grid-cols-2">
        {shown.map((text) => (
          <li key={text} className="tw-flex tw-min-w-0 tw-items-start tw-gap-2 tw-text-sm tw-text-content">
            <CheckCircle2 size={16} className="tw-mt-0.5 tw-shrink-0 tw-text-success" aria-hidden="true" />
            <span className="tw-min-w-0 tw-break-words">{text}</span>
          </li>
        ))}
      </ul>
      {items.length > HIGHLIGHTS_SHOWN && (
        <AppButton size="xs" variant="ghost" className="tw-mt-1 tw--ml-2" aria-expanded={all} onClick={() => setAll((a) => !a)}>
          {all ? "Show fewer" : `Show all ${items.length}`}
        </AppButton>
      )}
    </>
  );
};

/**
 * CourseAboutCard — what the course promises: its description, highlights,
 * prerequisites and skills, as the admin wrote them in the course form.
 *
 * Props:
 *   course     — the `GET /courses/:id` course
 *   highlights — courseDetails.courseHighlights()
 *   skills     — courseDetails.courseSkills()
 *   onEdit     — opens the course form; absent for someone who may not edit
 */
export const CourseAboutCard = ({ course, highlights, skills, onEdit }) => {
  const hasDescription = hasRichContent(course.description);
  const hasPrerequisites = hasRichContent(course.prerequisites);
  const empty = !hasDescription && !hasPrerequisites && !highlights.length && !skills.length;

  return (
    <AppCard as="section" aria-labelledby="course-about-title" className="tw-h-full">
      <AppCardHeader>
        <AppCardTitle id="course-about-title">About this course</AppCardTitle>
      </AppCardHeader>
      <AppCardContent className="tw-flex tw-flex-col tw-gap-5">
        {empty ? (
          <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
            <p className="tw-m-0 tw-text-sm tw-text-content-subtle">
              No description, highlights or prerequisites yet.
            </p>
            {onEdit && (
              <AppButton size="sm" variant="outline" onClick={onEdit}>
                <Pencil size={16} aria-hidden="true" /> Add them
              </AppButton>
            )}
          </div>
        ) : (
          <>
            {hasDescription && (
              <Expandable>
                <RichText value={course.description} className="tw-text-sm tw-leading-relaxed tw-text-content" />
              </Expandable>
            )}

            {highlights.length > 0 && (
              <div>
                <SectionTitle>Highlights</SectionTitle>
                <Highlights items={highlights} />
              </div>
            )}

            {hasPrerequisites && (
              <div>
                <SectionTitle>Prerequisites</SectionTitle>
                <RichText value={course.prerequisites} compact className="tw-text-sm tw-text-content" />
              </div>
            )}

            {skills.length > 0 && (
              <div>
                <SectionTitle>Skills</SectionTitle>
                <ul className="tw-m-0 tw-flex tw-list-none tw-flex-wrap tw-gap-1.5 tw-p-0">
                  {skills.map((skill) => (
                    <li
                      key={skill}
                      className="tw-rounded-[6px] tw-border tw-border-line tw-bg-surface-sunken tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-content-muted"
                    >
                      {skill}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </AppCardContent>
    </AppCard>
  );
};

/**
 * One labelled fact. A `<div>` holding a `<dt>`/`<dd>` pair, so it must sit in
 * a `<dl>`: the label is the term, the value its description.
 */
const Fact = ({ icon: Icon, label, children }) => (
  <div className="tw-min-w-0">
    <dt className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-medium tw-text-content-subtle">
      <Icon size={14} className="tw-shrink-0" aria-hidden="true" />
      {label}
    </dt>
    <dd className="tw-m-0 tw-mt-1 tw-break-words tw-text-sm tw-font-medium tw-text-content">
      {children ?? DASH}
    </dd>
  </div>
);

const Note = ({ children }) => (
  <span className="tw-block tw-text-xs tw-font-normal tw-text-content-subtle">{children}</span>
);

function offeredLine(offered) {
  if (offered.everywhere) return "Every branch";
  const named = offered.names.join(", ");
  if (!offered.hidden) return named || DASH;
  const more = `${offered.hidden} other ${offered.hidden === 1 ? "branch" : "branches"}`;
  return named ? `${named} and ${more}` : more;
}

/**
 * CourseFactsCard — the course's settings at a glance: what it costs, how long
 * it runs, how it is taught, and who may join it.
 *
 * Props:
 *   course      — the `GET /courses/:id` course
 *   fee         — courseDetails.courseFee()
 *   offered     — courseDetails.offeredAt(), or null for a single-branch institute
 *   showWebsite — the institute has a website, so "hidden from it" means something
 */
export const CourseFactsCard = ({ course, fee, offered, showWebsite }) => {
  const freeDemo = course.freeDemo ? String(course.freeDemo) : null;
  const priceHidden = showWebsite && course.showPricePublicly === false;

  return (
    <AppCard as="section" aria-labelledby="course-facts-title" className="tw-h-full">
      <AppCardHeader>
        <AppCardTitle id="course-facts-title">Details</AppCardTitle>
      </AppCardHeader>
      <AppCardContent>
        <dl className="tw-m-0 tw-grid tw-grid-cols-2 tw-gap-x-6 tw-gap-y-4 min-[768px]:tw-grid-cols-3 min-[1200px]:tw-grid-cols-2">
          <Fact icon={IndianRupee} label="Fee">
            {fee.text && (
              <>
                {fee.text}
                {priceHidden && <Note>Not shown on your website</Note>}
              </>
            )}
          </Fact>
          <Fact icon={Clock} label="Duration">{course.duration || null}</Fact>
          <Fact icon={Monitor} label="Mode">{course.mode || null}</Fact>
          <Fact icon={PlayCircle} label="Free demo">{freeDemo}</Fact>
          <Fact icon={Users} label="Max strength">{course.maxStrength || null}</Fact>
          <Fact icon={UserPlus} label="Self-enrolment">
            {course.allowSelfEnrollment ? "Students can request to join" : "Off"}
          </Fact>
          {offered && <Fact icon={MapPin} label="Offered at">{offeredLine(offered)}</Fact>}
          {course.enrollmentLink && (
            <Fact icon={ExternalLink} label="Enrolment link">
              <a
                href={course.enrollmentLink}
                target="_blank"
                rel="noopener noreferrer"
                className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-sm tw-text-primary tw-no-underline tw-focus-ring hover:tw-underline"
              >
                Open link <ExternalLink size={12} aria-hidden="true" />
                <span className="tw-sr-only"> (opens in a new tab)</span>
              </a>
            </Fact>
          )}
        </dl>
      </AppCardContent>
    </AppCard>
  );
};
